import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import {
  STORAGE_KEY_LOCALE,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  translations,
} from './translations';

function resolveKey(obj, path) {
  if (!obj || !path) return undefined;
  let cur = obj;
  for (const part of path.split('.')) {
    if (cur === undefined || cur === null) return undefined;
    cur = cur[part];
  }
  return cur;
}

function getInitialLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_LOCALE);
    if (raw && SUPPORTED_LOCALES.includes(raw)) return raw;
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_LOCALE;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY_LOCALE, next);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const bundle = translations[locale] || translations.tr;

  const t = useCallback(
    (key, fallback = key) => {
      const val = resolveKey(bundle, key);
      return typeof val === 'string' ? val : fallback;
    },
    [bundle]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
